import { addEdge } from '@xyflow/react';

/**
 * Créer un handler pour gérer les nouvelles connexions entre nœuds
 */
export const createHandleConnect = (
  nodes,
  setEdges,
  callFlowData,
  setCallFlowData,
  saveCallFlowToDatabase,
  handleEdgeDelete,
  handleEdgeLabelChange,
  previousCallFlowDataRef
) => {
  return (connection) => {
    const sourceNode = nodes.find(n => n.id === connection.source);
    const targetNode = nodes.find(n => n.id === connection.target);
    
    if (!sourceNode || !targetNode) {
      return;
    }

    // ==============================
    // GESTION SPÉCIALE : SWAP DU ROUTING 0
    // ==============================
    // Détecter si on connecte un nouveau routing vers le nœud source principal
    // (resource-account, greeting, holidays, open-hours)
    const isSourceMainNode = ['resource-account', 'greeting', 'holidays', 'open-hours'].includes(sourceNode.id);
    const isTargetNewRouting = targetNode.id.startsWith('routing-');
    
    if (isSourceMainNode && isTargetNewRouting) {
      const newRoutingKey = targetNode.id.replace('routing-', '');
      const existingRouting0 = callFlowData?.routing?.['0'];
      
      // Si un routing 0 existe déjà ET que le nouveau routing n'est pas déjà le routing 0
      if (existingRouting0 && newRoutingKey !== '0') {
        
        // 1. Générer un nouvel ID et routingName pour l'ancien routing 0 basé sur son type
        const timestamp = Date.now();
        const oldRouting0Type = existingRouting0.type || 'routing';
        const oldDisplayName = existingRouting0.displayName || 'Start';
        
        // Créer un nouveau displayName basé sur le type
        const typeDisplayNames = {
          'ivr': 'IVR',
          'prompt': 'Prompt',
          'check_variable': 'Check Variable',
          'time': 'Time',
          'check_days': 'Check Days',
          'regexp': 'RegExp',
          'queue': 'Queue',
          'transfer': 'Transfer',
          'disconnect': 'Disconnect'
        };
        
        const newDisplayNameForOld = typeDisplayNames[oldRouting0Type] || oldRouting0Type.charAt(0).toUpperCase() + oldRouting0Type.slice(1);
        
        // Créer un routingName descriptif basé sur le type et displayName
        // Utiliser le même format pour la clé ET le routingName
        const newRoutingNameForOld = `${newDisplayNameForOld}_${timestamp}`.replace(/\s+/g, '_');
        const newIdForOldRouting0 = newRoutingNameForOld; // Utiliser le même que routingName
        
        // 2. Récupérer les données du nouveau routing
        const newRoutingData = callFlowData.routing[newRoutingKey];
        
        if (!newRoutingData) {
          console.error('❌ New routing not found:', newRoutingKey);
          return;
        }
        
        // 3. Créer une copie du routing actuel avec les nouvelles clés
        const updatedRouting = { ...callFlowData.routing };
        
        // 4. L'ancien routing 0 devient un routing normal avec le nouvel ID et routingName
        updatedRouting[newIdForOldRouting0] = {
          ...existingRouting0,
          routingName: newRoutingNameForOld,
          displayName: newRoutingNameForOld // Le displayName doit être identique au routingName
        };
        
        // 5. Le nouveau routing prend la place du routing 0
        updatedRouting['0'] = {
          ...newRoutingData,
          routingName: '0',
          // On garde le displayName du nouveau routing mais on peut le changer si nécessaire
        };
        
        // 6. Supprimer l'ancien routing avec son ancienne clé
        delete updatedRouting[newRoutingKey];
        
        // 7. Mettre à jour toutes les références dans les autres routings
        // Parcourir tous les routings pour mettre à jour les références
        Object.keys(updatedRouting).forEach(key => {
          const routing = updatedRouting[key];
          
          // Fonction helper pour mettre à jour une option
          const updateOptionReference = (option) => {
            if (!option) return option;
            
            if (option.type === 'routing') {
              // Si l'option référence l'ancien routing 0, mettre à jour vers le nouveau nom
              if (option.routingName === '0') {
                return { ...option, routingName: newIdForOldRouting0 };
              }
              // Si l'option référence le nouveau routing, mettre à jour vers '0'
              if (option.routingName === newRoutingKey) {
                return { ...option, routingName: '0' };
              }
            }
            return option;
          };
          
          // Mettre à jour les options (pour IVR)
          if (routing.options) {
            const updatedOptions = {};
            Object.keys(routing.options).forEach(optKey => {
              updatedOptions[optKey] = updateOptionReference(routing.options[optKey]);
            });
            updatedRouting[key] = { ...routing, options: updatedOptions };
          }
          
          // Mettre à jour next (pour Prompt)
          if (routing.next) {
            updatedRouting[key] = { 
              ...updatedRouting[key], 
              next: updateOptionReference(routing.next) 
            };
          }
          
          // Mettre à jour onMatched et onUnMatched (pour Conditional)
          if (routing.onMatched) {
            updatedRouting[key] = { 
              ...updatedRouting[key], 
              onMatched: updateOptionReference(routing.onMatched) 
            };
          }
          if (routing.onUnMatched) {
            updatedRouting[key] = { 
              ...updatedRouting[key], 
              onUnMatched: updateOptionReference(routing.onUnMatched) 
            };
          }
        });
        
        // 8. Mettre à jour le callFlowData
        const updatedCallFlowData = {
          ...callFlowData,
          routing: updatedRouting
        };
        
        setCallFlowData(updatedCallFlowData);
        // NE PAS forcer la régénération ici pour permettre la création de l'edge
        // previousCallFlowDataRef.current = null;
        
        
        // Sauvegarder dans la base de données
        saveCallFlowToDatabase(updatedCallFlowData)
          .then(() => {
          })
          .catch((error) => {
            console.error('❌ Error saving routing swap:', error);
          });
        
        // Après le swap, ne pas retourner immédiatement
        // Continuer pour créer l'edge si nécessaire, mais ajuster la connexion
        // Le nouveau routing 0 (qui était newRoutingKey) doit maintenant se connecter à la cible
        // Mettre à jour la connexion pour pointer vers routing-0
        connection = {
          ...connection,
          source: 'routing-0',
          sourceHandle: connection.sourceHandle
        };
        
        // Mettre à jour sourceNode pour qu'il pointe vers le nouveau routing-0
        const updatedSourceNode = {
          ...sourceNode,
          id: 'routing-0',
          data: targetNode.data // Utiliser les données du nouveau routing
        };
        
        // Remplacer sourceNode dans le scope local
        // Note: on ne peut pas réassigner sourceNode directement car c'est une const,
        // mais on peut créer une nouvelle variable et l'utiliser ensuite
        // Pour l'instant, continuer le flux normalement
        // Le diagramme se régénérera et les nœuds seront recréés avec les bons IDs
        
        // Ne pas retourner, continuer pour créer l'edge
        // return; // <- Enlever ce return
      }
    }

    // Gestion spéciale pour les connexions vers le workflow
    if (targetNode.id.startsWith('workflow')) {
      // Extraire l'index du workflow (workflow-0, workflow-1, etc.)
      const workflowMatch = targetNode.id.match(/^workflow-?(\d*)$/);
      const workflowIndex = workflowMatch && workflowMatch[1] !== '' ? parseInt(workflowMatch[1]) : 0;
      
      const expectedTriggerNodeId = workflowIndex === 0 && targetNode.id === 'workflow' ? 'trigger-event-node' : `trigger-event-node-${workflowIndex}`;
      
      // Extraire le nom du nœud source pour le triggerEvent
      let triggerEventValue = '';
      
      if (sourceNode.id === expectedTriggerNodeId) {
        // Connexion depuis trigger-event-node vers workflow - activer le workflow
        const updatedWorkflow = [...(callFlowData.workflow || [])];
        if (updatedWorkflow[workflowIndex]) {
          updatedWorkflow[workflowIndex] = {
            ...updatedWorkflow[workflowIndex],
            enabled: true,
          };
        }
        
        const updated = {
          ...callFlowData,
          workflow: updatedWorkflow
        };
        
        setCallFlowData(updated);
        previousCallFlowDataRef.current = null;
        
        
        saveCallFlowToDatabase(updated)
          .then(() => {
          })
          .catch((error) => {
            console.error('❌ Error enabling workflow:', error);
          });
        
        // Créer l'edge
        const newEdge = {
          ...connection,
          type: 'custom',
          animated: true,
          style: { stroke: '#8b5cf6', strokeWidth: 2 },
          markerEnd: {
            type: 'arrowclosed',
            color: '#8b5cf6',
          },
          data: {
            onDelete: handleEdgeDelete,
            workflowIndex: workflowIndex,
          },
        };
        setEdges((eds) => addEdge(newEdge, eds));
        return;
      }
      
      // Connexion depuis un autre nœud vers workflow
      // Extraire le triggerEvent depuis le nœud source
      
      if (sourceNode.id.startsWith('routing-')) {
        const routingKey = sourceNode.id.replace('routing-', '');
        const routing = callFlowData?.routing?.[routingKey];
        if (routing) {
          // Utiliser routingName ou key ou displayName
          triggerEventValue = routing.routingName || routing.key || routing.displayName || routingKey;
          // Supprimer le timestamp si présent (format: _1234567890123)
          triggerEventValue = triggerEventValue.replace(/_\d{13}$/, '');
        }
      } else if (sourceNode.id === 'resource-account') {
        triggerEventValue = 'RESOURCE_ACCOUNT';
      } else if (sourceNode.id === 'greeting') {
        triggerEventValue = 'GREETING';
      } else if (sourceNode.id === 'open-hours') {
        triggerEventValue = 'OPEN_HOURS';
      } else if (sourceNode.id === 'holidays') {
        triggerEventValue = 'HOLIDAYS';
      } else {
        // Par défaut, utiliser l'ID du nœud et supprimer le timestamp
        triggerEventValue = sourceNode.id.toUpperCase().replace(/-/g, '_');
        triggerEventValue = triggerEventValue.replace(/_\d{13}$/, '');
      }


      // Mettre à jour le workflow avec triggerEvent et enabled
      const updatedWorkflow = [...(callFlowData.workflow || [])];
      if (updatedWorkflow[workflowIndex]) {
        updatedWorkflow[workflowIndex] = {
          ...updatedWorkflow[workflowIndex],
          enabled: true,
          triggerEvent: triggerEventValue
        };
      }

      const updated = {
        ...callFlowData,
        workflow: updatedWorkflow
      };


      setCallFlowData(updated);
      previousCallFlowDataRef.current = null;


      saveCallFlowToDatabase(updated)
        .then((response) => {
        })
        .catch((error) => {
          console.error('❌ Error saving workflow:', error);
        });

      return; // Ne pas créer d'edge, le diagramme se régénérera avec les nœuds intermédiaires
    }

    // Gestion spéciale pour les connexions vers le trigger-event-node
    if (targetNode.id.startsWith('trigger-event-node')) {
      // Extraire l'index du workflow
      const triggerMatch = targetNode.id.match(/^trigger-event-node-?(\d*)$/);
      const workflowIndex = triggerMatch && triggerMatch[1] !== '' ? parseInt(triggerMatch[1]) : 0;
      
      // Extraire le nom du nœud source pour le triggerEvent
      let triggerEventValue = '';
      
      if (sourceNode.id.startsWith('routing-')) {
        const routingKey = sourceNode.id.replace('routing-', '');
        const routing = callFlowData?.routing?.[routingKey];
        if (routing && routing.key) {
          triggerEventValue = routing.key;
        }
      } else if (sourceNode.id === 'resource-account') {
        triggerEventValue = 'resource-account';
      } else if (sourceNode.id === 'greeting') {
        triggerEventValue = 'greeting';
      } else if (sourceNode.id === 'open-hours') {
        triggerEventValue = 'open-hours';
      } else if (sourceNode.id === 'holidays') {
        triggerEventValue = 'holidays';
      }

      // Mettre à jour le workflow avec le nouveau triggerEvent
      setCallFlowData(prev => {
        const updatedWorkflow = [...(prev.workflow || [])];
        if (updatedWorkflow[workflowIndex]) {
          updatedWorkflow[workflowIndex] = {
            ...updatedWorkflow[workflowIndex],
            triggerEvent: triggerEventValue
          };
        }

        const updated = {
          ...prev,
          workflow: updatedWorkflow
        };

        // Réinitialiser la référence pour forcer la régénération
        previousCallFlowDataRef.current = null;


        // Sauvegarder dans la base de données
        saveCallFlowToDatabase(updated)
          .then(() => {
          })
          .catch((error) => {
            console.error('❌ Error saving workflow triggerEvent:', error);
          });

        return updated;
      });

      // Créer l'edge normalement
      const newEdge = {
        ...connection,
        type: 'custom',
        animated: true,
        style: { stroke: '#8b5cf6', strokeWidth: 2 },
        markerEnd: {
          type: 'arrowclosed',
          color: '#8b5cf6',
        },
      };

      setEdges((eds) => addEdge(newEdge, eds));
      return;
    }

    // Déterminer le label et le type selon le routing source
    let edgeLabel = '';
    let edgeType = 'custom';
    let edgeColor = 'rgb(98, 100, 167)';
    let needsDTMF = false;
    let needsMatchUnmatch = false;

    // Déterminer si c'est un IVR qui nécessite un DTMF
    if (connection.source.startsWith('routing-')) {
      const routingKey = connection.source.replace('routing-', '');
      const routing = callFlowData?.routing?.[routingKey];
      
      if (routing && (routing.type === 'ivr' || routing.type === 'IVR')) {
        // Pour IVR, on va demander le DTMF via l'input éditable
        edgeLabel = ''; // Vide au départ pour forcer l'édition
        edgeType = 'editable';
        edgeColor = '#6366f1';
        needsDTMF = true;
      } else if (routing && routing.type === 'prompt') {
        // Pour Prompt, pas besoin de DTMF, c'est le "next"
        edgeLabel = 'Next';
        edgeColor = '#3b82f6';
      } else if (routing && (routing.type === 'check_variable' || routing.type === 'time' || routing.type === 'check_days' || routing.type === 'regexp')) {
        // Pour Conditional, on va demander match/unmatch via l'input
        edgeLabel = ''; // Vide au départ
        edgeType = 'editable';
        edgeColor = '#8b5cf6';
        needsMatchUnmatch = true;
      }
    }

    // Déterminer la couleur selon le type de target
    if (targetNode.id.startsWith('queue-')) {
      edgeColor = '#10b981';
    } else if (targetNode.id === 'disconnect-node') {
      edgeColor = '#ef4444';
    } else if (targetNode.id.startsWith('transfer-user-')) {
      edgeColor = '#8b5cf6';
    } else if (targetNode.id.startsWith('transfer-pstn-')) {
      edgeColor = '#f97316';
    } else if (targetNode.id.startsWith('routing-')) {
      edgeColor = '#8b5cf6';
    }

    // Créer un edge temporaire qui permettra à l'utilisateur d'entrer le DTMF
    const newEdge = {
      ...connection,
      type: edgeType,
      animated: true,
      style: { stroke: edgeColor, strokeWidth: 1.5 },
      markerEnd: {
        type: 'arrowclosed',
        color: edgeColor,
      },
      label: edgeLabel,
      labelStyle: { fill: edgeColor, fontWeight: 600 },
      labelBgStyle: { fill: 'white' },
      data: {
        onDelete: handleEdgeDelete,
        onLabelChange: edgeType === 'editable' ? (edgeId, newLabel) => {
          // Créer l'option selon le type de target
          let option = null;
          
          // Si le target est un routing node (queue, transfer, disconnect, ou autre routing)
          if (targetNode.id.startsWith('routing-')) {
            const targetRoutingKey = targetNode.id.replace('routing-', '');
            const targetRouting = callFlowData?.routing?.[targetRoutingKey];
            
            if (!targetRouting) {
              return;
            }
            
            // Construire l'option - toujours utiliser type 'routing' pour référencer un routing existant
            // même si c'est une queue, transfer ou disconnect
            const routingName = targetRouting.routingName || targetRouting.displayName || targetRoutingKey;
            option = {
              type: 'routing',
              routingName
            };
          }

          // Mettre à jour le callFlowData
          if (connection.source.startsWith('routing-') && option) {
            const routingKey = connection.source.replace('routing-', '');
            const routing = callFlowData?.routing?.[routingKey];
            
            if (routing) {
              let updatedCallFlowData = null;
              
              if (routing.type === 'ivr' || routing.type === 'IVR') {
                // Ajouter à options avec le DTMF key
                setCallFlowData(prev => {
                  updatedCallFlowData = {
                    ...prev,
                    routing: {
                      ...prev.routing,
                      [routingKey]: {
                        ...prev.routing[routingKey],
                        options: {
                          ...(prev.routing[routingKey].options || {}),
                          [newLabel]: option,
                        },
                      },
                    },
                  };
                  // Réinitialiser la référence pour forcer la régénération
                  previousCallFlowDataRef.current = null;
                  return updatedCallFlowData;
                });
              } else if (routing.type === 'check_variable' || routing.type === 'time' || routing.type === 'check_days' || routing.type === 'regexp') {
                // Mettre à jour onMatched ou onUnMatched
                // Validation: accepter seulement 'match' ou 'unmatch'
                if (newLabel !== 'match' && newLabel !== 'unmatch') {
                  return;
                }
                
                const field = newLabel === 'match' ? 'onMatched' : 'onUnMatched';
                setCallFlowData(prev => {
                  updatedCallFlowData = {
                    ...prev,
                    routing: {
                      ...prev.routing,
                      [routingKey]: {
                        ...prev.routing[routingKey],
                        [field]: option,
                      },
                    },
                  };
                  // Réinitialiser la référence pour forcer la régénération
                  previousCallFlowDataRef.current = null;
                  return updatedCallFlowData;
                });
              }
              
              // Sauvegarder dans la base de données
              if (updatedCallFlowData) {
                saveCallFlowToDatabase(updatedCallFlowData)
                  .then((result) => {
                  })
                  .catch((error) => {
                  });
              }
            }
          }

          // Mettre à jour le label de l'edge dans le diagramme
          setEdges((eds) =>
            eds.map((e) =>
              e.id === edgeId ? { ...e, label: newLabel } : e
            )
          );
        } : null,
        needsInput: needsDTMF || needsMatchUnmatch, // Flag pour auto-focus l'input
        inputPlaceholder: needsMatchUnmatch ? 'match/unmatch' : 'DTMF',
      },
    };

    // Pour Prompt, sauvegarder immédiatement car pas besoin de label
    if (connection.source.startsWith('routing-') && edgeLabel === 'Next') {
      const routingKey = connection.source.replace('routing-', '');
      const routing = callFlowData?.routing?.[routingKey];
      
      if (routing && routing.type === 'prompt') {
        let option = null;
        
        // Si le target est un routing node
        if (targetNode.id.startsWith('routing-')) {
          const targetRoutingKey = targetNode.id.replace('routing-', '');
          const targetRouting = callFlowData?.routing?.[targetRoutingKey];
          
          if (!targetRouting) {
            return;
          }
          // Construire l'option - toujours utiliser type 'routing' pour référencer un routing existant
          const routingName = targetRouting.routingName || targetRouting.displayName || targetRoutingKey;
          option = {
            type: 'routing',
            routingName
          };
        }
        
        if (option) {
          const updatedCallFlowData = {
            ...callFlowData,
            routing: {
              ...callFlowData.routing,
              [routingKey]: {
                ...callFlowData.routing[routingKey],
                next: option,
              },
            },
          };
          
          setCallFlowData(updatedCallFlowData);
          
          // Réinitialiser la référence pour forcer la régénération du diagramme
          previousCallFlowDataRef.current = null;
          
          // Sauvegarder dans la base de données
          saveCallFlowToDatabase(updatedCallFlowData)
            .then(() => {
            })
            .catch((error) => {
              console.error('Error saving next connection:', error);
            });
        }
      }
    }

    setEdges((eds) => addEdge(newEdge, eds));
  };
};
